import * as state from '@/components/Analysis/state';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { abyssColors } from '@/theme/colors';
import { Stack, Typography } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PointOfInterestsFragment,
  ReviewActionChecklistConfig,
  useUpdateReportOrderMutation,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { DraggableList } from '@/components/shared/DraggableList';
import { generatePoiDisplayName } from '../../../modules/pointOfInterest/PointOfInterestTab/SelectedPoiContent/utils';
import { useComponentGroupTitles } from '../../../ReviewScreen/hooks/useComponentGroupTitles';
import { filterReviewActionsWithPois } from '../../../ReviewScreen/hooks/useFilterReviewActionPois';
import { useEquipmentList, usePointOfInterestDetails } from '../../hooks';
import {
  CurrentItemIcon,
  EquipmentName,
  EquipmentRowStack,
  NavigationContainer,
  NavigatorTooltip,
  POIName,
  PoiRowStack,
  SectionItem,
  SectionTitle,
} from '../styles';
import {
  computeReviewActionPoiIds,
  sortPointOfInterests,
  sortReviewActionPois,
} from './computeReviewActionPoiIds';
import { CustomSkeleton } from './styles';
import { ReportOrder } from './types';

const scrollToElement = (id: string) => {
  const element = document.querySelector(`#${CSS.escape(id)}`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const ListSkeleton = () => {
  return (
    <Stack gap={2}>
      <CustomSkeleton
        animation="wave"
        variant="text"
        width={90}
        height={24}
        sx={{ marginLeft: 4 }}
      />
      <Stack marginBottom="4px" marginLeft={5} gap={1.5}>
        <CustomSkeleton animation="wave" variant="text" width={90} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={60} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={70} height={16} />
        <CustomSkeleton animation="wave" variant="text" width={50} height={16} />
      </Stack>
    </Stack>
  );
};

const NavigatorSkeleton = () => {
  return (
    <Stack gap={6} marginBottom={6}>
      <Stack direction="row" alignItems="center" paddingX={3}>
        <CustomSkeleton animation="wave" variant="text" width={200} height={30} />
      </Stack>
      {Array.from({ length: 2 }).map((_, index) => (
        <ListSkeleton key={index} />
      ))}
    </Stack>
  );
};

type ComponentReportNavigatorProps = {
  setOrderUpdateLoader: (loader: boolean) => void;
  orderOfReport: ReportOrder;
  setOrderOfReport: React.Dispatch<React.SetStateAction<ReportOrder>>;
};

type EquipmentItem = {
  id: string;
  name: string;
};

export const ComponentReportNavigator = ({
  setOrderUpdateLoader,
  orderOfReport,
  setOrderOfReport,
}: ComponentReportNavigatorProps) => {
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined);
  const selectedReport = useAtomValue(state.selectedReport);
  const setSelectedReport = useSetAtom(state.selectedReport);
  const equipmentList = useEquipmentList(selectedReport);
  const groupTitles = useComponentGroupTitles();
  const navbarHeight = useNavbarHeight();
  const { groupPoiByEquipmentWithNumbers: equipmentAndPOIs } = usePointOfInterestDetails();
  const router = useRouter();
  const reportId = router?.query?.reportid as string | undefined;
  const [updateReportOrder] = useUpdateReportOrderMutation();

  useEffect(() => {
    if (Object.keys(equipmentList).length > 0 && !selectedItem) {
      setSelectedItem(Object.keys(equipmentList)[0]);
    }
  }, [equipmentList, selectedItem]);

  const handleClick = (item: string) => {
    setSelectedItem(item);
    scrollToElement(item);
  };

  const isLoading = useMemo(() => {
    return !groupTitles || !selectedReport;
  }, [groupTitles, selectedReport]);

  const specifications = useMemo(() => {
    const checklistConfig = (selectedReport?.components?.[0]?.template
      ?.reviewActionChecklistConfig || []) as ReviewActionChecklistConfig[];

    const reviewActionPois =
      (selectedReport as { reviewActionPois?: Record<string, unknown[]> | null })
        ?.reviewActionPois ?? undefined;

    const configsWithPois = filterReviewActionsWithPois(checklistConfig, reviewActionPois);

    const reviewActionChecklists = configsWithPois.map((config: ReviewActionChecklistConfig) => ({
      title: config.displayName,
      id: config.reviewActionValue,
    }));

    return [
      ...(groupTitles || []),
      ...reviewActionChecklists,
      {
        title: 'Attachments',
        id: 'attachments',
      },
    ];
  }, [groupTitles, selectedReport]);

  const hasData = useMemo(() => {
    return !!selectedReport?.pointOfInterests?.length || !!selectedReport?.components?.length;
  }, [selectedReport]);

  const equipmentItems: EquipmentItem[] = useMemo(
    () =>
      equipmentList
        .filter((equipment): equipment is NonNullable<typeof equipment> => !!equipment)
        .map((equipment) => ({ id: equipment.id, name: equipment.tagName ?? '' })),
    [equipmentList]
  );

  const getOrderedPOIs = useCallback(
    (assemblyId: string, tagName: string): PointOfInterestsFragment[] => {
      const pois = (equipmentAndPOIs[tagName] ?? []) as PointOfInterestsFragment[];
      const ids = orderOfReport?.poiOrder?.[assemblyId] ?? [];
      if (ids.length === 0) return pois;
      const map = new Map(pois.map((poi) => [poi.id, poi]));
      return ids.map((id) => map.get(id)).filter(Boolean) as PointOfInterestsFragment[];
    },
    [equipmentAndPOIs, orderOfReport]
  );

  const buildOrderPayload = (poiOrder: { [key: string]: string[] }): Record<string, string[]> =>
    Object.fromEntries(Object.entries(poiOrder).map(([equip, poiIds]) => [equip, poiIds]));

  const submitReportOrder = useCallback(
    (poiOrder: { [key: string]: string[] }, assemblyOrder: string[]) => {
      if (!reportId) return;
      setOrderUpdateLoader(true);
      const poiOrderObject = buildOrderPayload(poiOrder);
      const currentReviewActionPois = (selectedReport as { reviewActionPois?: unknown } | undefined)
        ?.reviewActionPois;
      const reviewActionPoiIds = computeReviewActionPoiIds(
        currentReviewActionPois,
        assemblyOrder,
        poiOrderObject
      );
      const optimisticReviewActionPois = sortReviewActionPois(
        currentReviewActionPois,
        assemblyOrder,
        poiOrderObject
      );

      setSelectedReport((prev) => {
        if (!prev) return prev;
        const sortedPointOfInterests = sortPointOfInterests(
          prev.pointOfInterests,
          assemblyOrder,
          poiOrderObject
        );
        return {
          ...prev,
          pointOfInterests:
            sortedPointOfInterests.length > 0 ? sortedPointOfInterests : prev.pointOfInterests,
          reportOrder: {
            assemblyOrder,
            poiOrder: poiOrderObject,
          },
          reviewActionPois:
            Object.keys(optimisticReviewActionPois).length > 0
              ? optimisticReviewActionPois
              : prev.reviewActionPois,
        };
      });

      try {
        updateReportOrder({
          variables: {
            input: {
              reportId,
              reportOrder: {
                assemblyOrder,
                poiOrder: poiOrderObject,
              },
              reviewActionPoiIds,
            },
          },
        })
          .then((response) => {
            setSelectedReport((prev) => {
              if (!prev) return prev;
              const updated = response.data?.updateReport;
              return {
                ...prev,
                reportOrder: {
                  assemblyOrder:
                    updated?.reportOrder?.assemblyOrder ?? prev.reportOrder?.assemblyOrder,
                  poiOrder: updated?.reportOrder?.poiOrder ?? prev.reportOrder?.poiOrder,
                },
                reviewActionPois: updated?.reviewActionPois ?? prev.reviewActionPois,
              };
            });
          })
          .finally(() => {
            setOrderUpdateLoader(false);
          });
      } catch (e) {
        console.error('Failed to save report order', e);
      }
    },
    [reportId, selectedReport, updateReportOrder, setOrderUpdateLoader, setSelectedReport]
  );

  const handleEquipmentOrderChange = useCallback(
    (newOrder: EquipmentItem[]) => {
      const assemblyOrder = newOrder.map((e) => e.id);
      const updatedOrder = {
        ...orderOfReport,
        assemblyOrder,
      };
      setOrderOfReport(updatedOrder);
      submitReportOrder(orderOfReport?.poiOrder || {}, assemblyOrder);
    },
    [orderOfReport, setOrderOfReport, submitReportOrder]
  );

  const handlePoiOrderChange = useCallback(
    (assemblyId: string) => (newOrder: PointOfInterestsFragment[]) => {
      if (!assemblyId) return;
      const updated = {
        ...orderOfReport,
        poiOrder: {
          ...orderOfReport?.poiOrder,
          [assemblyId]: newOrder.map((poi) => poi.id),
        },
      };
      setOrderOfReport(updated);
      submitReportOrder(updated.poiOrder, updated.assemblyOrder);
    },
    [orderOfReport, setOrderOfReport, submitReportOrder]
  );

  return (
    <NavigationContainer navbarHeight={navbarHeight} data-testid="report-navigation">
      <Typography
        color={abyssColors.primary[700]}
        fontWeight={700}
        m={3}
        data-testid="report-navigator-title"
      >
        Contents
      </Typography>
      {isLoading && hasData && <NavigatorSkeleton />}
      {!isLoading && hasData && (
        <DraggableList
          items={equipmentItems}
          onChange={handleEquipmentOrderChange}
          renderItem={(equipment) => {
            const tagName = equipment.name;
            const orderedPOIs = getOrderedPOIs(equipment.id, tagName);
            return (
              <DraggableList.Item id={equipment.id}>
                <Stack gap={2} marginBottom={6}>
                  <EquipmentRowStack direction="row" alignItems="center">
                    <CurrentItemIcon selected={selectedItem === tagName} />
                    <NavigatorTooltip title={tagName} placement="top">
                      <EquipmentName
                        selected={selectedItem === tagName}
                        onClick={() => handleClick(tagName)}
                        sx={{ flex: 1, minWidth: 0, marginLeft: 1 }}
                      >
                        {tagName}
                      </EquipmentName>
                    </NavigatorTooltip>
                    <DraggableList.DragHandle className="drag-handle" />
                  </EquipmentRowStack>

                  <Stack data-testid="report-navigator-equipment">
                    <SectionTitle selected>Specifications</SectionTitle>
                    {specifications?.map((detail) => (
                      <Stack
                        key={detail.title}
                        direction="row"
                        alignItems="center"
                        marginBottom="4px"
                        data-testid="report-navigator-detail"
                      >
                        <CurrentItemIcon selected={selectedItem === `${tagName}-${detail.title}`} />
                        <SectionItem
                          selected={selectedItem === `${tagName}-${detail.title}`}
                          onClick={() => {
                            handleClick(`${tagName}-${detail.title}`);
                          }}
                        >
                          {detail.title}
                        </SectionItem>
                      </Stack>
                    ))}

                    {orderedPOIs.length > 0 && (
                      <>
                        <SectionTitle selected>Point of interests</SectionTitle>
                        <DraggableList
                          items={orderedPOIs}
                          onChange={handlePoiOrderChange(equipment.id)}
                          renderItem={(point) => (
                            <DraggableList.Item id={point.id}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                marginBottom="4px"
                                data-testid="report-navigator-poi"
                              >
                                <CurrentItemIcon selected={selectedItem === point.id} />
                                <PoiRowStack direction="row" alignItems="center">
                                  <NavigatorTooltip
                                    title={generatePoiDisplayName({
                                      name: point.name,
                                      poiType: point.type,
                                      poiId: point.poiId,
                                    })}
                                    placement="top"
                                  >
                                    <POIName
                                      selected={selectedItem === point.id}
                                      onClick={() => {
                                        handleClick(point.id);
                                      }}
                                      sx={{ marginLeft: '3.2rem' }}
                                    >
                                      {generatePoiDisplayName({
                                        name: point.name,
                                        poiType: point.type,
                                        poiId: point.poiId,
                                      })}
                                    </POIName>
                                  </NavigatorTooltip>
                                  <DraggableList.DragHandle className="drag-handle" />
                                </PoiRowStack>
                              </Stack>
                            </DraggableList.Item>
                          )}
                        />
                      </>
                    )}
                  </Stack>
                </Stack>
              </DraggableList.Item>
            );
          }}
        />
      )}
    </NavigationContainer>
  );
};
